import { useCallback, useRef } from 'react';
import * as Sentry from "@sentry/react";

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

const useAnalytics = (userId: string = 'anonymous') => {
  const isInitialized = useRef(false);

  const initAnalytics = useCallback(
    (tag: string) => {
      return new Promise<void>((resolve, reject) => {
        if (isInitialized.current) {
          console.warn('Google Analytics is already initialized.');
          return resolve();
        }

        try {
          const script = document.createElement('script');
          script.async = true;
          script.src = `https://www.googletagmanager.com/gtag/js?id=${tag}`;
          script.onerror = (error) => {
            console.error('Failed to load Google Analytics script:', error);
            reject(new Error('GA script failed to load.'));
          };
          document.head.appendChild(script);

          window.dataLayer = window.dataLayer || [];
          // eslint-disable-next-line
          function gtag() {
            window.dataLayer.push(arguments);
          }
          window.gtag = gtag;

          script.onload = () => {
            try {
              console.log('id for GA config', userId);
              window.gtag('js', new Date());
              window.gtag('config', tag, {
                user_id: userId,
                send_page_view: false,
              });

              isInitialized.current = true;
              resolve();
            } catch (error) {
              console.error('GA script loaded, but initialization failed:', error);
              reject(error);
            }
          };
        } catch (error) {
          console.error('Unexpected error while initializing Google Analytics:', error);
          reject(error);
        }
      });
    },
    [isInitialized, userId]
  );

  const sendEvent = useCallback(
    (eventName: string, eventParams: { [key: string]: any } = {}) => {
      if (!isInitialized.current) {
        console.warn('Google Analytics is not initialized. Call init() first.');
        return;
      }

      try {
        eventParams['user_id'] = userId;
        if (typeof window.gtag !== 'function') {
          throw new Error('gtag function is not available.');
        }
        window.gtag('event', eventName, eventParams);
      } catch (error) {
        console.error(`Failed to send GA event "${eventName}":`, error);
        Sentry.captureException(new Error(`GA event failure: ${eventName} - ${error.message}`));
      }
    },
    [isInitialized, userId]
  );

  return { initAnalytics, sendEvent };
};

export default useAnalytics;

